// apps/prompts/src/hooks/useKeyboardShortcuts.ts
import { useEffect, useCallback, useRef } from 'react'

interface ShortcutConfig {
  key: string
  ctrl?: boolean
  meta?: boolean
  shift?: boolean
  alt?: boolean
  handler: () => void
  /** Set true to allow the shortcut even when typing in an input/textarea */
  allowInInput?: boolean
  description?: string
}

// ─── Global registry for help modal ───
const shortcutRegistry: Array<{ keys: string; description: string }> = []

export function getRegisteredShortcuts() {
  return [...shortcutRegistry]
}

export function useKeyboardShortcuts(shortcuts: ShortcutConfig[]) {
  const shortcutsRef = useRef(shortcuts)
  shortcutsRef.current = shortcuts

  useEffect(() => {
    // Register for help display
    const entries = shortcuts
      .filter((s) => s.description)
      .map((s) => ({
        keys: formatShortcutKeys(s),
        description: s.description!,
      }))

    entries.forEach((e) => {
      if (!shortcutRegistry.some((r) => r.keys === e.keys)) {
        shortcutRegistry.push(e)
      }
    })

    return () => {
      entries.forEach((e) => {
        const idx = shortcutRegistry.findIndex((r) => r.keys === e.keys)
        if (idx !== -1) shortcutRegistry.splice(idx, 1)
      })
    }
  }, [shortcuts])

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const target = event.target as HTMLElement
    const isInput =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable

    for (const shortcut of shortcutsRef.current) {
      if (!shortcut.allowInInput && isInput) continue

      const ctrlMatch = shortcut.ctrl ? event.ctrlKey || event.metaKey : true
      const metaMatch = shortcut.meta ? event.metaKey : true
      const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey
      const altMatch = shortcut.alt ? event.altKey : !event.altKey

      if (
        event.key.toLowerCase() === shortcut.key.toLowerCase() &&
        ctrlMatch &&
        metaMatch &&
        shiftMatch &&
        altMatch
      ) {
        event.preventDefault()
        event.stopPropagation()
        shortcut.handler()
        return
      }
    }
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])
}

function formatShortcutKeys(s: ShortcutConfig): string {
  const parts: string[] = []
  if (s.ctrl || s.meta) parts.push(navigator.platform.includes('Mac') ? '⌘' : 'Ctrl')
  if (s.shift) parts.push('⇧')
  if (s.alt) parts.push(navigator.platform.includes('Mac') ? '⌥' : 'Alt')
  parts.push(s.key.toUpperCase())
  return parts.join('+')
}
